import { notify } from '@affine/component';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import { ServersService } from '@affine/core/modules/cloud';
import { WorkspaceDialogService } from '@affine/core/modules/dialogs';
import { FolderTemplateService } from '@affine/core/modules/template-doc';
import {
  type WorkspaceMetadata,
  WorkspacesService,
} from '@affine/core/modules/workspace';
import {
  buildShowcaseWorkspace,
  type WorkspaceProjectData,
} from '@affine/core/utils/first-app-data';
import { UserFriendlyError } from '@affine/error';
import { ErrorNames } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import {
  useLiveData,
  useService,
  useServiceOptional,
} from '@toeverything/infra';
import { useState } from 'react';

import {
  waitForTemplateData,
  type WorkspaceTemplateOption,
} from './use-workspace-templates';

export interface CreatedWorkspacePayload {
  metadata: WorkspaceMetadata;
  defaultDocId?: string;
}

/**
 * Creates a workspace straight from a template pick, with no intermediate
 * dialog: `null` seeds the onboarding showcase ("empty project"), a template
 * option copies that folder subtree into a brand new workspace.
 *
 * The name is derived from the template — everything else (description,
 * client, deadline, tags) stays editable through the full create dialog.
 */
export const useCreateWorkspaceFromTemplate = (
  onCreated?: (payload: CreatedWorkspacePayload) => void
) => {
  const t = useI18n();
  const serversService = useService(ServersService);
  // Project type is fixed to OrgHub Cloud (affine-cloud) for all workspaces.
  const server = useLiveData(serversService.server$('affine-cloud'));
  const workspacesService = useService(WorkspacesService);
  // may be unavailable outside a workspace scope (e.g. landing page)
  const workspaceDialogService = useServiceOptional(WorkspaceDialogService);
  const [creating, setCreating] = useState(false);

  const createWorkspace = useAsyncCallback(
    async (
      template: WorkspaceTemplateOption | null,
      projectData?: WorkspaceProjectData
    ) => {
      if (creating) return;
      setCreating(true);
      const flavour = server?.id ?? 'local';
      const workspaceName =
        template?.folderName ?? t['com.affine.nameWorkspace.default-name']();
      try {
        if (template) {
          // seed the new workspace from a folder template
          const opened = workspacesService.openByWorkspaceId(
            template.workspaceId
          );
          if (!opened) {
            throw new Error(
              `Source workspace ${template.workspaceId} is not available`
            );
          }
          let metadata;
          try {
            // resolving the service connects the workspace db sub-docs the
            // folder tree lives in; they load asynchronously afterwards
            const folderTemplateService = opened.workspace.scope.get(
              FolderTemplateService
            );
            await waitForTemplateData(opened.workspace);
            metadata = await folderTemplateService.instantiateAsWorkspace(
              template.folderId,
              workspaceName,
              flavour,
              projectData
            );
          } finally {
            opened.dispose();
          }
          onCreated?.({ metadata });
        } else {
          const res = await buildShowcaseWorkspace(
            workspacesService,
            flavour,
            workspaceName,
            projectData
          );
          onCreated?.({
            metadata: res.meta,
            defaultDocId: res.defaultDocId,
          });
        }
      } catch (e) {
        console.error(e);
        const err = UserFriendlyError.fromAny(e);
        if (err.name === ErrorNames.WORKSPACE_LIMIT_REACHED) {
          notify.error({
            title: t['com.affine.workspace.create.limit-reached.title'](),
            message: t['com.affine.workspace.create.limit-reached.message'](),
          });
          workspaceDialogService?.open('setting', {
            activeTab: 'plans',
            scrollAnchor: 'cloudPricingPlan',
          });
        } else {
          notify.error({
            title: t['com.affine.workspace.failed-to-create'](),
            message: t['com.affine.please-try-again-later'](),
          });
        }
      } finally {
        setCreating(false);
      }
    },
    [creating, onCreated, server, t, workspaceDialogService, workspacesService]
  );

  return { creating, createWorkspace };
};
